import { bool, number, oneOf, oneOfType, string } from "prop-types";
import { useEffect, useRef, useState } from "react";

import styled from "styled-components";
import {
  border,
  borderRadius,
  height,
  themeSizes,
  themeVariants,
  width,
} from "theme";
import { widthBySize } from "theme/constants";

import { unoBackupSrc } from "Flocknote/constants";

import FnrOpacityToggle from "UI/FnrOpacityToggle";
import { colors } from "UI/colors";

import borderFlash from "Utilities/interpolations/borderFlash";

const SFnrOpacityToggle = styled(FnrOpacityToggle)`
  line-height: 0px;
`;

const Simg = styled("img")`
  ${borderRadius}
  ${border}
  ${width}
  ${height}
  ${borderFlash}
  background-color: ${colors.white};
  object-fit: cover;
`;

const circle = "circle";
const square = "square";

/**
 * @param {object} param0
 * @param {string} param0.src - the url of the image
 * @param {size} param0.size - the size of the image
 * @param {"square"|"circle"} param0.shape - the shape of the image
 * @param {string} param0.color - the color of the border/background
 * @param {boolean} param0.skipFadeIn - show the photo from its first render instead of fading it in once it loads. For a photo drawn over an identical one already on screen, where the fade reads as a flicker.
 * @returns
 */
const FnrNetworkPhoto = ({
  src,
  size = themeSizes.md,
  width: widthArg,
  shape = circle,
  color = "white",
  thick = true,
  noBorder,
  isFlashing,
  flashToColor,
  skipFadeIn,
  ...rest
}) => {
  const [loadFailed, setLoadFailed] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const isMountedRef = useRef();

  // if the source changes, try again.
  useEffect(() => {
    if (!isMountedRef.current) {
      isMountedRef.current = true;
      return;
    }
    setLoadFailed(false);
    setIsLoading(true);
  }, [src]);

  return (
    <SFnrOpacityToggle show={skipFadeIn || !isLoading} {...rest}>
      <Simg
        $size={size}
        $width={widthArg}
        $height={widthBySize[size] || widthArg}
        aria-label="Network photo"
        $variant={
          shape === circle ? themeVariants.icon : themeVariants.container
        }
        $isFlashing={isFlashing}
        $flashToColor={flashToColor}
        $color={color}
        $thick={thick}
        $noBorder={noBorder}
        src={(loadFailed ? unoBackupSrc : src) || unoBackupSrc}
        onError={() => {
          setLoadFailed(true);
          setIsLoading(false);
        }}
        onLoad={() => {
          setIsLoading(false);
        }}
      />
    </SFnrOpacityToggle>
  );
};

FnrNetworkPhoto.propTypes = {
  src: string,
  size: oneOf(Object.values(themeSizes)),
  width: number,
  shape: oneOf([square, circle]),
  color: string,
  thick: oneOfType([bool, number]),
  noBorder: bool,
  isFlashing: bool,
  flashToColor: string,
  skipFadeIn: bool,
};

export default FnrNetworkPhoto;
